import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Package, Clock, ArrowUp, ArrowDown,
  BarChart3, Banknote, Landmark, TrendingUp, CheckCircle2, ArrowRight,
  MapPin, Calendar, Users, Wallet, Receipt, ShoppingCart, 
  ArrowUpRight, ArrowDownRight, Activity
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import api from '../lib/api';

const fmtNum = (v) => Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtRs = (v) => `Rs. ${fmtNum(v)}`;

/* ─── Custom Tooltip for Chart ─── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl shadow-lg px-4 py-3">
      <p className="text-xs text-[#6b7280] font-medium mb-1">{label}</p>
      <p className="text-sm font-bold text-[#1a1a2e]">{fmtRs(payload[0].value)}</p>
    </div>
  );
};

/* ─── Mini stat card for Today's Pulse ─── */
const PulseCard = ({ icon: Icon, label, value, trend, trendUp, onClick, accent }) => (
  <div 
    onClick={onClick}
    className={`bg-white rounded-2xl border border-[#e5e7eb] p-5 shadow-sm hover:shadow-md transition-all cursor-pointer ${onClick ? 'hover:border-[#4A7AE0]/30' : ''}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center`} style={{ background: `${accent}15` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      {trend !== undefined && (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${trendUp ? 'bg-[#DBEAFE] text-[#4A7AE0]' : 'bg-[#FEF2F2] text-[#DC2626]'}`}>
          {trendUp ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <p className="text-xs font-medium text-[#6b7280] uppercase tracking-wider mb-1">{label}</p>
    <p className="text-xl font-bold text-[#1a1a2e] tracking-tight">{value}</p>
  </div>
);

/* ─── KPI Card (vertical, icon left) ─── */
const KpiCard = ({ icon: Icon, label, value, subtext, accent, onClick }) => (
  <div 
    onClick={onClick}
    className={`bg-white rounded-2xl border border-[#e5e7eb] p-5 shadow-sm hover:shadow-md transition-all ${onClick ? 'cursor-pointer hover:border-[#4A7AE0]/30' : ''}`}
  >
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}12` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-[#6b7280] uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-[#1a1a2e] tracking-tight mt-0.5">{value}</p>
        {subtext && <p className="text-xs text-[#9ca3af] mt-1">{subtext}</p>}
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ total_sales: 0, total_purchase: 0, stock_items: 0, pending_payments: 0 });
  const [salesStats, setSalesStats] = useState({ total_sales_this_month: 0, outstanding_receivables: 0 });
  const [acctStats, setAcctStats] = useState({ cash_balance: 0, bank_balance: 0, total_receivables: 0, total_payables: 0 });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [monthGlance, setMonthGlance] = useState({ sales_this_month: 0, purchases_this_month: 0, net_profit_this_month: 0, most_sold_product: 'N/A' });
  const [agingSummary, setAgingSummary] = useState(null);
  const [todaySummary, setTodaySummary] = useState({ today_sales: 0, today_purchases: 0, today_expenses: 0, today_net: 0 });
  const [salesTrend, setSalesTrend] = useState([]);
  const [pendingActions, setPendingActions] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) { navigate('/login'); return; }
    setUser(JSON.parse(storedUser));
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      const [
        statsResponse,
        lowStockResponse,
        salesStatsResponse,
        recentInvoicesResponse,
        acctStatsResponse,
        monthGlanceResponse,
        agingResponse,
        todayResponse,
        salesTrendResponse,
        pendingResponse,
      ] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/products/low-stock/list?limit=5'),
        api.get('/sales/stats'),
        api.get('/sales/recent-invoices?limit=5'),
        api.get('/accounts/dashboard-stats').catch(() => ({ data: { cash_balance: 0, bank_balance: 0, total_receivables: 0, total_payables: 0 } })),
        api.get('/reports/month-glance').catch(() => ({ data: { sales_this_month: 0, purchases_this_month: 0, net_profit_this_month: 0, most_sold_product: 'N/A' } })),
        api.get('/dashboard/aging-summary').catch(() => ({ data: null })),
        api.get('/dashboard/today-summary').catch(() => ({ data: { today_sales: 0, today_purchases: 0, today_expenses: 0, today_net: 0 } })),
        api.get('/dashboard/sales-trend?days=30').catch(() => ({ data: [] })),
        api.get('/dashboard/pending-actions').catch(() => ({ data: null })),
      ]);
      setStats(statsResponse.data);
      setLowStockProducts(lowStockResponse.data);
      setSalesStats(salesStatsResponse.data);
      setRecentInvoices(recentInvoicesResponse.data);
      setAcctStats(acctStatsResponse.data);
      setMonthGlance(monthGlanceResponse.data);
      setAgingSummary(agingResponse.data);
      setTodaySummary(todayResponse.data);
      setSalesTrend(salesTrendResponse.data);
      setPendingActions(pendingResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f0f2f5]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full animate-spin border-[3px] border-[#4A7AE0]/20 border-t-[#4A7AE0]" />
          <p className="text-[13px] text-[#64748B]">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  const tenantName = user?.company_name || user?.full_name || 'Dashboard';
  const netProfit = monthGlance.net_profit_this_month || 0;
  const monthlySales = salesStats.total_sales_this_month || monthGlance.sales_this_month || 0;
  const cashBalance = acctStats.cash_balance || 0;
  const bankBalance = acctStats.bank_balance || 0;
  const receivables = acctStats.total_receivables || stats.total_receivables || 0;
  const payables = acctStats.total_payables || 0;
  const todayNet = todaySummary.today_net || 0;

  const today = new Date();
  const daysUntil = (d) => d ? Math.floor((new Date(d) - today) / (1000 * 60 * 60 * 24)) : null;
  const relDueLabel = (d) => {
    const n = daysUntil(d);
    if (n === null) return '';
    if (n < 0) return `Due ${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'} ago`;
    if (n === 0) return 'Due today';
    return `Due in ${n} day${n === 1 ? '' : 's'}`;
  };

  const outstandingInvoices = (recentInvoices || []).filter((inv) => inv.status !== 'paid').slice(0, 4);

  const fixItems = [];
  if (pendingActions?.overdue_invoices_count > 0) {
    fixItems.push({
      key: 'overdue', Icon: AlertTriangle, accent: '#DC2626',
      name: 'Overdue payments', subtitle: `${pendingActions.overdue_invoices_count} invoice${pendingActions.overdue_invoices_count > 1 ? 's' : ''} past due`,
      amount: fmtNum(pendingActions.overdue_invoices_amount || 0),
      onClick: () => navigate('/dashboard/reports/aging'),
    });
  }
  if (pendingActions?.low_stock_count > 0) {
    fixItems.push({
      key: 'low-stock', Icon: Package, accent: '#D97706',
      name: 'Low stock', subtitle: `${pendingActions.low_stock_count} item${pendingActions.low_stock_count > 1 ? 's' : ''} need reorder`,
      amount: `${pendingActions.low_stock_count}`,
      onClick: () => navigate('/inventory'),
    });
  }
  if (pendingActions?.unconfirmed_po_count > 0) {
    fixItems.push({
      key: 'upcoming-payments', Icon: Clock, accent: '#4A7AE0',
      name: 'Upcoming payments', subtitle: `${pendingActions.unconfirmed_po_count} supplier bill${pendingActions.unconfirmed_po_count > 1 ? 's' : ''} due`,
      amount: `${pendingActions.unconfirmed_po_count} due`,
      onClick: () => navigate('/purchase/orders'),
    });
  }

  const now = new Date();
  const greeting = () => {
    const hour = now.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const chartData = salesTrend.length > 0 ? salesTrend : Array.from({ length: 30 }, (_, i) => ({
    label: `Day ${i + 1}`, sales: 0
  }));

  return (
    <div className="flex min-h-screen bg-[#f0f2f5]" data-testid="dashboard-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} user={user} />

      <div className={`flex-1 min-w-0 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : ''}`}>
        <Navbar user={user} toggleSidebar={toggleSidebar} />

        <main className="flex-1 p-6 lg:p-8 max-w-[1440px] mx-auto w-full" data-testid="dashboard-content">
          {/* ── Header Row ── */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1a1a2e] tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="welcome-message">
                {greeting()}, <span className="text-[#4A7AE0]">{tenantName}</span>
              </h1>
              <div className="flex items-center gap-3 mt-2 text-sm text-[#6b7280]">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {now.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <span className="text-[#d1d5db]">|</span>
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  {user?.city || 'Karachi, Pakistan'}
                </span>
              </div>
            </div>
          </div>

          {/* ── Today's Pulse ── */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-[#4A7AE0]" />
              <h2 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wider">Today's Pulse</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <PulseCard
                icon={Wallet}
                label="Cash In"
                value={fmtRs(todaySummary.today_sales)}
                trend={12.5}
                trendUp={true}
                accent="#4A7AE0"
                onClick={() => navigate('/sales/invoices')}
              />
              <PulseCard
                icon={ShoppingCart}
                label="Purchases"
                value={fmtRs(todaySummary.today_purchases)}
                accent="#7C3BD6"
              />
              <PulseCard
                icon={Receipt}
                label="Expenses"
                value={fmtRs(todaySummary.today_expenses)}
                accent="#E0922E"
              />
              <PulseCard
                icon={todayNet >= 0 ? ArrowUpRight : ArrowDownRight}
                label="Net Movement"
                value={fmtRs(Math.abs(todayNet))}
                accent={todayNet >= 0 ? '#0FA968' : '#DC2626'}
              />
            </div>
          </div>

          {/* ── Chart + KPIs ── */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
            {/* Sales Trend Chart */}
            <div className="xl:col-span-2 bg-white rounded-2xl border border-[#e5e7eb] shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-base font-semibold text-[#1a1a2e]">Sales Trend</h3>
                  <p className="text-xs text-[#9ca3af] mt-0.5">Last 30 days performance</p>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-3 h-3 rounded-full bg-gradient-to-r from-[#22B8D4] to-[#4A7AE0]" />
                  <span className="text-[#6b7280] font-medium">Daily Sales</span>
                </div>
              </div>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4A7AE0" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#4A7AE0" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis 
                      dataKey="label" 
                      tick={{ fontSize: 11, fill: '#9ca3af' }} 
                      axisLine={false} 
                      tickLine={false}
                      interval={4}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#9ca3af' }} 
                      axisLine={false} 
                      tickLine={false}
                      tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="sales"
                      stroke="#4A7AE0"
                      strokeWidth={2.5}
                      fill="url(#salesGradient)"
                      dot={false}
                      activeDot={{ r: 5, strokeWidth: 0, fill: '#4A7AE0' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* KPI Stack */}
            <div className="space-y-4">
              <KpiCard
                icon={BarChart3}
                label="Monthly Sales"
                value={fmtRs(monthlySales)}
                subtext="This month's revenue"
                accent="#4A7AE0"
                onClick={() => navigate('/sales/invoices')}
              />
              <KpiCard
                icon={Banknote}
                label="Cash Balance"
                value={fmtRs(cashBalance)}
                subtext="Available cash on hand"
                accent="#22B8D4"
              />
              <KpiCard
                icon={Landmark}
                label="Bank Balance"
                value={fmtRs(bankBalance)}
                subtext="Total across all bank accounts"
                accent="#7C3BD6"
              />
              <KpiCard
                icon={TrendingUp}
                label="Net Profit"
                value={fmtRs(netProfit)}
                subtext={netProfit >= 0 ? 'Profit this month' : 'Loss this month'}
                accent={netProfit >= 0 ? '#0FA968' : '#DC2626'}
              />
            </div>
          </div>

          {/* ── Bottom Row: Receivables + Payables + Fix Things ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Receivables */}
            <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm p-6" data-testid="receivables-card">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base font-semibold text-[#1a1a2e]">Receivables</h3>
                  <p className="text-2xl font-bold text-[#1a1a2e] mt-1">{fmtRs(receivables)}</p>
                </div>
                <button onClick={() => navigate('/sales/invoices')} className="text-xs font-medium text-[#4A7AE0] hover:underline flex items-center gap-1">
                  View all <ArrowRight className="w-3 h-3" />
                </button>
              </div>

              {outstandingInvoices.length === 0 ? (
                <p className="text-sm text-[#9ca3af] py-4">No outstanding receivables.</p>
              ) : (
                <div className="space-y-3">
                  {outstandingInvoices.map((inv) => {
                    const days = daysUntil(inv.due_date);
                    const overdue = days !== null && days < 0;
                    return (
                      <div key={inv.id} className="flex items-center justify-between py-2 border-b border-[#f3f4f6] last:border-0">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#1a1a2e] truncate">{inv.customer?.name || 'Walk-in'}</p>
                          <p className="text-xs text-[#9ca3af]">{inv.invoice_number} • {relDueLabel(inv.due_date)}</p>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <p className={`text-sm font-semibold ${overdue ? 'text-[#D97706]' : 'text-[#1a1a2e]'}`}>{fmtNum(inv.total_amount)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Payables */}
            <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm p-6" data-testid="payables-card">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base font-semibold text-[#1a1a2e]">Payables</h3>
                  <p className="text-2xl font-bold text-[#1a1a2e] mt-1">{fmtRs(payables)}</p>
                </div>
                <button onClick={() => navigate('/purchase/orders')} className="text-xs font-medium text-[#4A7AE0] hover:underline flex items-center gap-1">
                  View all <ArrowRight className="w-3 h-3" />
                </button>
              </div>

              {payables > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-[#f9fafb]">
                    <div className="w-9 h-9 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                      <Users className="w-4 h-4 text-[#4A7AE0]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#1a1a2e]">Supplier Balance</p>
                      <p className="text-xs text-[#6b7280]">Outstanding to suppliers</p>
                    </div>
                    <span className="text-sm font-bold text-[#1a1a2e]">{fmtRs(payables)}</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-[#f9fafb]">
                    <div className="w-9 h-9 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-[#4A7AE0]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#1a1a2e]">Status</p>
                      <p className="text-xs text-[#6b7280]">Payment schedule</p>
                    </div>
                    <span className="text-xs font-semibold text-[#4A7AE0] bg-[#DBEAFE] px-2.5 py-1 rounded-full">On track</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[#9ca3af] py-4">No outstanding payables.</p>
              )}
            </div>

            {/* Fix Things Today */}
            <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden" data-testid="fix-these-today">
              <div className="h-1 bg-gradient-to-r from-[#22B8D4] via-[#7C3BD6] to-[#EF4444]" />
              <div className="p-6">
                <h3 className="text-base font-semibold text-[#1a1a2e]">Fix These Today</h3>
                <p className="text-xs text-[#6b7280] mt-1">
                  {fixItems.length} thing{fixItems.length === 1 ? '' : 's'} need{fixItems.length === 1 ? 's' : ''} attention
                </p>

                {fixItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#22B8D4] to-[#7C3BD6] flex items-center justify-center mb-3 shadow-lg shadow-[#4A7AE0]/20">
                      <CheckCircle2 className="w-7 h-7 text-white" />
                    </div>
                    <p className="text-sm font-semibold text-[#1a1a2e]">All clear!</p>
                    <p className="text-xs text-[#9ca3af] mt-1 text-center">Nothing needs your attention.</p>
                  </div>
                ) : (
                  <div className="space-y-3 mt-4">
                    {fixItems.map((item) => {
                      const Ico = item.Icon;
                      return (
                        <div
                          key={item.key}
                          onClick={item.onClick}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#f9fafb] cursor-pointer transition-colors"
                        >
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${item.accent}12` }}>
                            <Ico className="w-5 h-5" style={{ color: item.accent }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#1a1a2e]">{item.name}</p>
                            <p className="text-xs text-[#9ca3af] truncate">{item.subtitle}</p>
                          </div>
                          <span className="text-sm font-bold shrink-0" style={{ color: item.accent }}>{item.amount}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
